import AppConfig from "../../config/AppConfig";
import * as constants from "../../constants/_pickerValues";
import {
  CONN_STRING_TYPE,
  CONNECTION_TYPES,
  EXTRACT_TYPES,
  LOAD_TYPES,
  TEST_CONNECTION_TYPES
} from "../../constants/_pickerValues";
import {ISecret} from "../../store/dataSource/types";
import {IUser} from "../../store/groups/types";
import {IFormField} from "./globalTypes";

// Used by data sources, data targets, to ensure names are clean, eg. contains only alphanumeric characters and underscores

export const cleanName = (secret: string, delimiter: string = "-") => {
  return secret.replace(/[\W_]+/g, delimiter);
};

// Generate secret name specifically for S3

export const generateSecretNameS3 = (
  screen: string,
  bucket: string = "",
  path: string
) => {
  let secretName: string = "";

  screen = screen.trim();
  bucket = bucket.trim();
  path = path.trim();

  secretName = cleanName(
    secretName.concat("s3-", screen, "-", bucket, "-", path)
  );

  return secretName;
};

// Generate secret name specifically for Azure Data Lake (ADLS)

export const generateSecretNameADLS = (
  accountName: string,
  container: string
) => {
  //cs-adls-target-<account_name (lowercase, cleaned - replace all special characters with -)>-<container (lowercase, cleaned)>

  let secretName: string = "";

  accountName = accountName.trim();
  container = container.trim();

  secretName = cleanName(
    secretName.concat("cs-adls-source-", accountName, "-", container),
    "-"
  );

  return secretName;
};

//Generate secret name for Foundry
export const generateSecretNameFoundry = (
  hostName: string,
) => {
  //cs-adls-target-<account_name (lowercase, cleaned - replace all special characters with -)>-<container (lowercase, cleaned)>

  let secretName: string = "";

  hostName = hostName.trim();

  secretName = cleanName(
    secretName.concat("cs-foundry-source-", hostName),
    "-"
  );

  return secretName;
};

// Generate secret name for connection types (excluding S3)

export const generateSecretName = (
  connection: string,
  screen: string,
  endpoint: string,
  database: string
) => {
  let secretName: string = "";

  screen = screen.trim();
  endpoint = endpoint.trim();
  database = database.trim();

  switch (connection) {
    case CONNECTION_TYPES.oracle:
      secretName = cleanName(
        secretName.concat("cs-oracle-", screen, "-", endpoint, "-", database)
      );
      break;
    case CONNECTION_TYPES.postGres:
      secretName = cleanName(
        secretName.concat("cs-postgres-", screen, "-", endpoint, "-", database)
      );
      break;
    case CONNECTION_TYPES.mssql:
      secretName = cleanName(
        secretName.concat("cs-mssql-", screen, "-", endpoint, "-", database)
      );
      break;
    case CONNECTION_TYPES.redShift:
      secretName = cleanName(
        secretName.concat(
          "cs-redshift-",
          screen,
          "-",
          endpoint.split(".")[0],
          "-",
          database
        )
      );
      break;
    default:
      break;
  }

  return secretName;
};

// Generate layerType
// ['EXTRACT_S3', 'EXTRACT_ADLS', 'EXTRACT_MSSQL', 'EXTRACT_WFS', 'EXTRACT_POSTGRES', 'EXTRACT_ORACLE']

export const generateLayerType = (connection: string) => {
  let layerType: string = "";

  switch (connection) {
    case CONNECTION_TYPES.oracle:
      layerType = EXTRACT_TYPES.oracle;
      break;
    case CONNECTION_TYPES.postGres:
      layerType = EXTRACT_TYPES.postGres;
      break;
    case CONNECTION_TYPES.mssql:
      layerType = EXTRACT_TYPES.mssql;
      break;
    case CONNECTION_TYPES.windowsFileshare:
      layerType = EXTRACT_TYPES.windowsFileshare;
      break;
    case CONNECTION_TYPES.s3Buckets:
      layerType = EXTRACT_TYPES.s3Buckets;
      break;
    case CONNECTION_TYPES.redShift:
      layerType = EXTRACT_TYPES.redShift;
      break;
    case CONNECTION_TYPES.azure:
      layerType = EXTRACT_TYPES.adls;
      break;
    case CONNECTION_TYPES.foundry:
      layerType = EXTRACT_TYPES.foundry;
      break;
    default:
      break;
  }

  return layerType;
};

// Generate layerTypeTarget
// ['LOAD_S3', 'LOAD_ADLS', 'LOAD_MSSQL', 'LOAD_WFS', 'LOAD_POSTGRES', 'LOAD_ORACLE']

export const generateLayerTypeTarget = (connection: string) => {
  let layerType: string = "";

  switch (connection) {
    case CONNECTION_TYPES.oracle:
      layerType = LOAD_TYPES.oracle;
      break;
    case CONNECTION_TYPES.postGres:
      layerType = LOAD_TYPES.postGres;
      break;
    case CONNECTION_TYPES.mssql:
      layerType = LOAD_TYPES.mssql;
      break;
    case CONNECTION_TYPES.windowsFileshare:
      layerType = LOAD_TYPES.windowsFileshare;
      break;
    case CONNECTION_TYPES.s3Buckets:
      layerType = LOAD_TYPES.s3Buckets;
      break;
    case CONNECTION_TYPES.redShift:
      layerType = LOAD_TYPES.redShift;
      break;
    case CONNECTION_TYPES.azure:
      layerType = LOAD_TYPES.adls;
      break;
    default:
      break;
  }

  return layerType;
};

// Generate connection from LayerType for Target
export const generateConnectionTarget = (layer: string) => {
  let connectionType: string = "";

  switch (layer) {
    case LOAD_TYPES.oracle:
      connectionType = CONNECTION_TYPES.oracle;
      break;
    case LOAD_TYPES.postGres:
      connectionType = CONNECTION_TYPES.postGres;
      break;
    case LOAD_TYPES.mssql:
      connectionType = CONNECTION_TYPES.mssql;
      break;
    case LOAD_TYPES.windowsFileshare:
      connectionType = CONNECTION_TYPES.windowsFileshare;
      break;
    case LOAD_TYPES.s3Buckets:
      connectionType = CONNECTION_TYPES.s3Buckets;
      break;
    case LOAD_TYPES.redShift:
      connectionType = CONNECTION_TYPES.redShift;
      break;
    case LOAD_TYPES.azure:
      connectionType = CONNECTION_TYPES.azure;
      break;
    default:
      break;
  }

  return connectionType;
};

// Generate Extract connectionType

export const generateConnectionType = (layerType: string) => {
  let connectionType: string = "";

  switch (layerType) {
    case EXTRACT_TYPES.adls:
      connectionType = CONNECTION_TYPES.azure;
      break;
    case EXTRACT_TYPES.oracle:
      connectionType = CONNECTION_TYPES.oracle;
      break;
    case EXTRACT_TYPES.postGres:
      connectionType = CONNECTION_TYPES.postGres;
      break;
    case EXTRACT_TYPES.mssql:
      connectionType = CONNECTION_TYPES.mssql;
      break;
    case EXTRACT_TYPES.windowsFileshare:
      connectionType = CONNECTION_TYPES.windowsFileshare;
      break;
    case EXTRACT_TYPES.s3Buckets:
      connectionType = CONNECTION_TYPES.s3Buckets;
      break;
    case EXTRACT_TYPES.redShift:
      connectionType = CONNECTION_TYPES.redShift;
      break;
    case EXTRACT_TYPES.foundry:
      connectionType = CONNECTION_TYPES.foundry;
      break;
    case EXTRACT_TYPES.azure:
      connectionType = CONNECTION_TYPES.azure;
      break;
    default:
      break;
  }

  return connectionType;
};

// Generate type for testing connection
// ['AzureSqlDatabase', 'Oracle', 'PostgreSql']

export const generateTestConnectionType = (connection: string) => {
  let connectionType: string = "";

  switch (connection) {
    case CONNECTION_TYPES.oracle:
      connectionType = TEST_CONNECTION_TYPES.oracle;
      break;
    case CONNECTION_TYPES.postGres:
      connectionType = TEST_CONNECTION_TYPES.postGres;
      break;
    case CONNECTION_TYPES.mssql:
      connectionType = TEST_CONNECTION_TYPES.mssql;
      break;
    case CONNECTION_TYPES.redShift:
      connectionType = TEST_CONNECTION_TYPES.redShift;
      break;
    case CONNECTION_TYPES.azure:
      connectionType = "AzureBlobStorage";
      break;
    default:
      break;
  }

  return connectionType;
};

// Generate connection strings, for both Synapse and App

export const generateConnectionString = (
  connection: string,
  connStringType: string,
  endpoint: string,
  database: string,
  port: string,
  username: string,
  password: string,
  serviceName?: string,
  instanceName?: string,
  accountName?: string,
  pathPrefix?: string,
  container?: string,
  sasToken?: string,
  accountKey?: string,
  hostName?: string,
  accessToken?: string,

) => {
  let connString: string = "";

  endpoint = endpoint.trim();
  database = database.trim();
  username = username.trim();
  port = port.trim();

  switch (connection) {
    case CONNECTION_TYPES.oracle:
      connString =
        connStringType === CONN_STRING_TYPE.synapse
          ? `Host=${endpoint};Port=${port.toString()};Servicename=${serviceName};User Id=${username};Password=${password};`
          : `Driver={Oracle in instantclient_21_3};Dbq=(DESCRIPTION=(ADDRESS = (PROTOCOL = TCP)(HOST = ${endpoint})(PORT = ${port}))(CONNECT_DATA =(SERVICE_NAME=${serviceName})));uid=${username};pwd=${password};Database=${database};`;

      break;

    case CONNECTION_TYPES.postGres:
      connString =
        connStringType === CONN_STRING_TYPE.synapse
          ? `Server=${endpoint};Database=${database};Port=${port.toString()};Uid=${username};Pwd=${password};EncryptionMethod=1;ValidateServerCertificate=0;`
          : `Driver={PostgreSQL UNICODE};Server=${endpoint};database=${database};port=5432;sslmode=require;uid=${username};password=${password};`;

      break;

    case CONNECTION_TYPES.mssql:
      connString =
        connStringType === CONN_STRING_TYPE.synapse
          ? `Data Source=${endpoint};Initial Catalog=${database};Integrated Security=False;User ID=${username};Password=${password};`
          : `Driver={SQL Server Native Client 11.0};Server=${endpoint};uid=${username};pwd=${password};Database=${database};`;

      break;

    case CONNECTION_TYPES.extSpoke:
      connString = `DefaultEndpointsProtocol=https;AccountName=${accountName};AccountKey=${accountKey};EndpointSuffix=core.windows.net`;
      break;

    case CONNECTION_TYPES.lndspoke:
      connString =
        connStringType === CONN_STRING_TYPE.synapse
          ? port === "" ? `Server=${endpoint},1433;Initial Catalog=${database};Integrated Security=False;User ID=${username};Password=${password}` : `Server=${endpoint},${port};Initial Catalog=${database};Integrated Security=False;User ID=${username};Password=${password}`
          : `Driver={SQL Server Native Client 11.0};Server=${endpoint};uid=${username};pwd=${password};Database=${database};Schema=LND`;
      break;

    case CONNECTION_TYPES.hstgspoke:
      connString =
        connStringType === CONN_STRING_TYPE.synapse
          ? port === "" ? `Server=${endpoint},1433;Initial Catalog=${database};Integrated Security=False;User ID=${username};Password=${password}` : `Server=${endpoint},${port};Initial Catalog=${database};Integrated Security=False;User ID=${username};Password=${password}`
          : `Driver={SQL Server Native Client 11.0};Server=${endpoint};uid=${username};pwd=${password};Database=${database};Schema=HSTG`;
      break;

    case CONNECTION_TYPES.redShift:
      connString =
        connStringType === CONN_STRING_TYPE.synapse
          ? `Server=${endpoint};Database=${database};Username=${username};Password=${password};Port=${port.toString()};`
          : `Driver={Amazon Redshift (x64)};Server=${endpoint};Database=${database};UID=${username};PWD=${password};Port=${port.toString()};`;
      break;

    case CONNECTION_TYPES.azure:
      connString =
        connStringType === CONN_STRING_TYPE.synapse
          ? accountKey
            ? `AccountName=${accountName};Container=${container};Path=${pathPrefix};AccountKey=${accountKey};`
            : `AccountName=${accountName};Container=${container};Path=${pathPrefix};SASToken=${sasToken};`
          : "";
      break;

    case CONNECTION_TYPES.foundry:
      connString = `jdbc:foundrysql://${hostName}?Password=${accessToken}`;
      break;
  }

  return connString;
};

export const getFormField = (value: string) => {
  let field: IFormField = {
    fieldValue: value,
    isError: false,
    errorMsg: "",
    isNumber: false,
  };

  return field;
};

// Generate secrets array for all connections (includes keys and values)

export const generateSecretArray = (
  connection: string,
  secretName: string,
  endpoint: string,
  database: string,
  port: string,
  username: string,
  password: string,
  accountKey?: string,
  accountName?: string,
  container?: string,
  sasToken?: string,
  winPath?: string,
  shareName?: string,
  winUsername?: string,
  winPassword?: string,
  serviceName?: string,
  hostName?: string,
  accessToken?: string
) => {
  let secrets: ISecret[] = [];
  let secret1: ISecret = { name: "", value: "" };
  let secret2: ISecret = { name: "", value: "" };
  let secret3: ISecret = { name: "", value: "" };

  endpoint = endpoint.trim();
  database = database.trim();
  username = username.trim();
  port = port.trim();
  accountKey = accountKey?.trim();
  accountName = accountName?.trim();
  container = container?.trim();
  winPath = winPath?.trim();
  shareName = shareName?.trim();
  winUsername = winUsername?.trim();

  switch (connection) {
    case CONNECTION_TYPES.s3Buckets:
      secret1.name = `accesskeyid-${secretName}`;
      secret1.value = AppConfig().AccessKeyId;

      secrets.push(secret1);

      secret2.name = `secretaccesskey-${secretName}`;
      secret2.value = AppConfig().SecretAccessKey;

      secrets.push(secret2);

      break;

    case CONNECTION_TYPES.azure:
      secret1.name = secretName;
      secret1.value = accountKey
        ? `${accountKey}`
        : `https://${accountName}.blob.core.windows.net/${container}/${sasToken}`;
      secrets.push(secret1);

      break;

    case CONNECTION_TYPES.windowsFileshare:
      if (winPath) {
        secret1.name = `host-filesystem-source---${cleanName(winPath)}`;
        secret1.value = shareName;
        secrets.push(secret1);

        secret2.name = `user-filesystem-source---${cleanName(winPath)}`;
        secret2.value = winUsername;
        secrets.push(secret2);

        secret3.name = `password-filesystem-source---${cleanName(winPath)}`;
        secret3.value = winPassword;
        secrets.push(secret3);
      }

      break;

    case CONNECTION_TYPES.oracle:
      secret1.name = secretName;
      secret1.value = generateConnectionString(
        connection,
        CONN_STRING_TYPE.synapse,
        endpoint,
        database,
        port,
        username,
        password,
        serviceName
      );
      secrets.push(secret1);

      secret2.name = secretName + "-dif";
      secret2.value = generateConnectionString(
        connection,
        CONN_STRING_TYPE.app,
        endpoint,
        database,
        port,
        username,
        password,
        serviceName
      );
      secrets.push(secret2);

      break;

    case CONNECTION_TYPES.lndspoke:
      secret1.name = secretName;
      secret1.value = generateConnectionString(
        connection,
        CONN_STRING_TYPE.synapse,
        endpoint,
        database,
        port,
        username,
        password,
        serviceName
      );
      secrets.push(secret1);

      secret2.name = secretName + "-dif";
      secret2.value = generateConnectionString(
        connection,
        CONN_STRING_TYPE.app,
        endpoint,
        database,
        port,
        username,
        password,
        serviceName
      );
      secrets.push(secret2);

      break;

    case CONNECTION_TYPES.redShift:
      secret1.name = secretName;
      secret1.value = generateConnectionString(
        connection,
        CONN_STRING_TYPE.synapse,
        endpoint,
        database,
        port,
        username,
        password
      );
      secrets.push(secret1);

      secret2.name = secretName + "-dif";
      secret2.value = generateConnectionString(
        connection,
        CONN_STRING_TYPE.app,
        endpoint,
        database,
        port,
        username,
        password
      );
      secrets.push(secret2);

      break;

    case CONNECTION_TYPES.foundry:
      secret1.name = secretName;
      secret1.value = `jdbc:foundrysql://${hostName}?Password=${accessToken}`;
      secrets.push(secret1);

      secret2.name = secretName + "-dif";
      secret2.value = `jdbc:foundrysql://${hostName}?Password=${accessToken}`;
      secrets.push(secret2);
      break;

    default:
      secret1.name = secretName;
      secret1.value = generateConnectionString(
        connection,
        CONN_STRING_TYPE.synapse,
        endpoint,
        database,
        port,
        username,
        password
      );
      secrets.push(secret1);

      secret2.name = secretName + "-dif";
      secret2.value = generateConnectionString(
        connection,
        CONN_STRING_TYPE.app,
        endpoint,
        database,
        port,
        username,
        password
      );
      secrets.push(secret2);

      break;
  }

  return secrets;
};

// Generate connection strings, for both Synapse and App

export const generateRedshiftSecretValue = (
  connection: string,
  connStringType: string,
  endpoint: string,
  database: string,
  port: string,
  username: string,
  password: string,
  serviceName?: string,
  instanceName?: string,
  accountName?: string,
  pathPrefix?: string,
  container?: string,
  sasToken?: string,
  accountKey?: string
) => {
  let connString: string = "";

  endpoint = endpoint.trim().split(".")[0];
  database = database.trim();
  username = username.trim();
  port = port.trim();

  let str = `{"cluster_id":"${endpoint}","database":"${database}","user":"${username}"}`;
  // let convertedValue = str.replace("\\\"", "\"");

  switch (connection) {
    case CONNECTION_TYPES.redShift:
      connString =
        connStringType === CONN_STRING_TYPE.synapse
          ? str
          : `Driver={Amazon Redshift (x64)}; Server=${endpoint}; Database=${database}; UID=${username}; PWD=${password}; Port=${port}`;
      break;
  }

  return connString;
};

export const generateRedshiftSecret = (
  connection: string,
  secretName: string,
  endpoint: string,
  database: string,
  port: string,
  username: string,
  password: string,
  accountKey?: string,
  accountName?: string,
  container?: string,
  sasToken?: string,
  winPath?: string,
  shareName?: string,
  winUsername?: string
) => {
  let secrets: ISecret[] = [];
  let secret1: ISecret = { name: "", value: "" };
  let secret2: ISecret = { name: "", value: "" };

  endpoint = endpoint.trim();
  database = database.trim();
  username = username.trim();
  port = port.trim();

  switch (connection) {
    default:
      secret1.name = secretName;
      secret1.value = generateRedshiftSecretValue(
        connection,
        CONN_STRING_TYPE.synapse,
        endpoint,
        database,
        port,
        username,
        password
      );
      secrets.push(secret1);

      secret2.name = secretName + "-dif";
      secret2.value = generateRedshiftSecretValue(
        connection,
        CONN_STRING_TYPE.app,
        endpoint,
        database,
        port,
        username,
        password
      );
      secrets.push(secret2);

      break;
  }

  return secrets;
};

// export const generateEXTSPokeSecret = (accountName: string, accountKey: string, accountURL: string) => {
//   connstring =
// }

export const getStewardsIDString = (ids: IUser[]) => {
  let idStr: string = "";

  ids &&
    ids.forEach((e: IUser) => {
      idStr = idStr.concat(e.userName, ";");
    });

  return idStr;
};

export const getStewardIDs = (ids: string) => {
  let dataStewards: IUser[] = [];
  if (ids !== null) {
    var splitIDs = ids.split(";").filter((x) => x !== "");

    splitIDs.forEach(function (value) {
      let user: IUser = { userName: value };
      dataStewards.push(user);
    });
  }
  return dataStewards;
};

export const getIUser = (userName: string) => {
  let user: IUser = { userName: userName };

  return user;
};

export const getIUsers = (userNames: string[]) => {
  let users: IUser[] = [];
  if (userNames !== undefined) {
    if (userNames.length === 1 && userNames[0] === "") {
    } else userNames.map((user) => users.push({ userName: user }));
  }
  return users;
};

export const getParam = (param: string[] | undefined | string, paramType: string) => {
  let str: string = "";
  if (param && param?.length > 0) str = `&${paramType}=${param}`;
  return str;
};


export const getBooleanParam = (param: boolean | null | undefined, paramType: string) => {
  let str: string = '';
  if (param) {
    str = `&${paramType}=${param}`;
  }
  return str;
}

export const getSearchTerm = (param: string | undefined, paramType: string) => {
  let str: string = "";
  if (param && param?.length > 0) str = `&${paramType}=${param}`;
  return str;
};

export function capitalizeFirstLetter(str: string) {
  // converting first letter to uppercase
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function convertUTCDateToLocalDate(date: any) {
  const dateLocal = new Date(date);
  return new Date(
      dateLocal.getTime() - dateLocal.getTimezoneOffset() * 60 * 1000
  );
}

export function getSecurityClassificationText(value: string) {
  if (value === constants.SECURITY_CLASS_VALUES.general) {
    return constants.SECURITY_CLASS.general
  } else if (value === constants.SECURITY_CLASS_VALUES.confidential) {
    return constants.SECURITY_CLASS.confidential
  } else if (value === constants.SECURITY_CLASS_VALUES.highlyConfidential) {
    return constants.SECURITY_CLASS.highlyConfidential
  }
}

export function getSecurityPayloadText(value: string) {
  if (value === constants.SECURITY_CLASS.general) {
    return constants.SECURITY_CLASS_VALUES.general
  } else if (value === constants.SECURITY_CLASS.confidential) {
    return constants.SECURITY_CLASS_VALUES.confidential
  } else if (value === constants.SECURITY_CLASS.highlyConfidential) {
    return constants.SECURITY_CLASS_VALUES.highlyConfidential
  }
}

export function getPrivacyClassificationText(value: string) {
  if (value === constants.PRIVACY_CLASS_VALUES.none) {
    return constants.PRIVACY_CLASS.none
  } else if (value === constants.PRIVACY_CLASS_VALUES.personal) {
    return constants.PRIVACY_CLASS.personal
  } else if (value === constants.PRIVACY_CLASS_VALUES.sensitive) {
    return constants.PRIVACY_CLASS.sensitive
  }
}

export function getPrivacyPayloadText(value: string) {
  if (value === constants.PRIVACY_CLASS.none) {
    return constants.PRIVACY_CLASS_VALUES.none
  } else if (value === constants.PRIVACY_CLASS.personal) {
    return constants.PRIVACY_CLASS_VALUES.personal
  } else if (value === constants.PRIVACY_CLASS.sensitive) {
    return constants.PRIVACY_CLASS_VALUES.sensitive
  }
}

export function getRequestStatusText(value: string) {
  if (value === constants.REQUEST_STATUS_VALUES.awaiting_review) {
    return constants.REQUEST_STATUS.awaiting_review
  } else if (value === constants.REQUEST_STATUS_VALUES.requested) {
    return constants.REQUEST_STATUS.requested
  } else if (value === constants.REQUEST_STATUS_VALUES.awaiting_approval) {
    return constants.REQUEST_STATUS.awaiting_approval
  } else if (value === constants.REQUEST_STATUS_VALUES.approved) {
    return constants.REQUEST_STATUS.approved
  } else if (value === constants.REQUEST_STATUS_VALUES.rejected) {
    return constants.REQUEST_STATUS.rejected
  } else if (value === constants.REQUEST_STATUS_VALUES.completed) {
    return constants.REQUEST_STATUS.completed
  } else if (value === constants.REQUEST_STATUS_VALUES.cancelled) {
    return constants.REQUEST_STATUS.cancelled
  } else if (value === constants.REQUEST_STATUS_VALUES.pending_notification) {
    return constants.REQUEST_STATUS.pending_notification
  } else if (value === constants.REQUEST_STATUS_VALUES.pending_completion) {
    return constants.REQUEST_STATUS.pending_completion
  } else {
    return ''
  }
}

export function getRequestStatusPayloadText(value: string) {
  if (value === constants.REQUEST_STATUS.awaiting_review) {
    return constants.REQUEST_STATUS_VALUES.awaiting_review
  } else if (value === constants.REQUEST_STATUS.requested) {
    return constants.REQUEST_STATUS_VALUES.requested
  } else if (value === constants.REQUEST_STATUS.awaiting_approval) {
    return constants.REQUEST_STATUS_VALUES.awaiting_approval
  } else if (value === constants.REQUEST_STATUS.approved) {
    return constants.REQUEST_STATUS_VALUES.approved
  } else if (value === constants.REQUEST_STATUS.rejected) {
    return constants.REQUEST_STATUS_VALUES.rejected
  } else if (value === constants.REQUEST_STATUS.completed) {
    return constants.REQUEST_STATUS_VALUES.completed
  } else if (value === constants.REQUEST_STATUS.cancelled) {
    return constants.REQUEST_STATUS_VALUES.cancelled
  } else if (value === constants.REQUEST_STATUS.pending_notification) {
    return constants.REQUEST_STATUS_VALUES.pending_notification
  } else if (value === constants.REQUEST_STATUS.pending_completion) {
    return constants.REQUEST_STATUS_VALUES.pending_completion
  } else {
     return ''
  }
};